import {
  Box,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  Spinner,
  Checkbox,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiEye,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import { debounce } from "lodash";

const RegistersEditor = ({ register, onClose, isNew }) => {
  const [registerHeader, setRegisterHeader] = useState({});
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (register) {
      setRegisterHeader({ ...register });
    }
  }, [register]);

  // Fetch entries separately from new API
  useEffect(() => {
    const fetchEntries = async () => {
      if (!register || isNew) {
        setEntries([]);
        return;
      }

      setEntriesLoading(true);
      try {
        const response = await fetch(
          `https://registers.poc.mrc.ai/api/registerentries?registerId=${register._id}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch register entries");
        }
        const data = await response.json();
        setEntries(data);
      } catch (error) {
        console.error("Error fetching entries:", error);
        toast({
          title: "Failed to load entries",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setEntriesLoading(false);
      }
    };

    fetchEntries();
  }, [register, isNew, toast]);

  const handleHeaderChange = useMemo(
    () =>
      debounce((field, value) => {
        setRegisterHeader((prev) => {
          if (prev[field] === value) return prev;
          return { ...prev, [field]: value };
        });
      }, 300),
    []
  );

  const handleSave = async () => {
    try {
      // Save only register header (no entries)
      const response = await fetch(
        isNew
          ? "https://registers.poc.mrc.ai/api/registers"
          : `https://registers.poc.mrc.ai/api/registers/${registerHeader._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(registerHeader),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "save"} register. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Register created" : "Register saved",
        description: isNew
          ? "New register was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/registers/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving register:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the register.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const response = await fetch(
        `https://registers.poc.mrc.ai/api/registerentries/${entryId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete entry");
      }

      setEntries((prev) => prev.filter((e) => e._id !== entryId));

      toast({
        title: "Entry deleted",
        description: "Register entry was deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("❌ Error deleting entry:", error);
      toast({
        title: "Delete failed",
        description: error.message || "An error occurred while deleting the entry.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleAddEntry = () => {
    if (!registerHeader._id) {
      toast({
        title: "Cannot add entry",
        description: "Please save the register first before adding entries.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    navigate(`/registers/${registerHeader._id}/entry/new`);
  };

  const handleEditEntry = (entryId) => {
    navigate(`/registers/${registerHeader._id}/entry/${entryId}`);
  };

  // ag-Grid column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Edit",
        field: "_id",
        flex: 0.5,
        cellRenderer: (params) => {
          return (
            <HStack spacing={2} height="100%" alignItems="center">
              <Tooltip label="Edit Entry">
                <IconButton
                  icon={<HiEye />}
                  size="sm"
                  onClick={() => handleEditEntry(params.value)}
                  aria-label="Edit entry"
                />
              </Tooltip>
            </HStack>
          );
        },
      },
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Type",
        field: "type",
        flex: 1,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Description",
        field: "description",
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Delete",
        field: "_id",
        flex: 0.5,
        cellRenderer: (params) => {
          return (
            <HStack spacing={2} height="100%" alignItems="center">
              <Tooltip label="Delete Entry">
                <IconButton
                  icon={<HiOutlineTrash />}
                  size="sm"
                  onClick={() => handleDeleteEntry(params.value)}
                  aria-label="Delete entry"
                />
              </Tooltip>
            </HStack>
          );
        },
      },
    ],
    []
  );

  const handleClose = () => {
    navigate("/Poc");
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Register</Heading>
                <Tooltip
                  label="Save Register"
                  aria-label="Save Register Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <VStack spacing={6} align="stretch">
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <UuidPreview uuid={registerHeader._id} />
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={registerHeader.name || ""}
                  onChange={(e) => handleHeaderChange("name", e.target.value)}
                />
              </FormControl>

              <CodesetSelect
                label="Type"
                codesetCode="B57"
                value={
                  registerHeader.type
                    ? { code: registerHeader.type, description: "" }
                    : null
                }
                onChange={(val) => handleHeaderChange("type", val.code)}
              />

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={registerHeader.description || ""}
                  onChange={(e) =>
                    handleHeaderChange("description", e.target.value)
                  }
                />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>As Of Date</FormLabel>
                <Input
                  value={registerHeader.asOfDateTime || ""}
                  onChange={(e) =>
                    handleHeaderChange("asOfDateTime", e.target.value)
                  }
                />
              </FormControl>
              <FormControl mt={4}>
                <Checkbox
                  isChecked={registerHeader.isDefinitionLibrary || false}
                  onChange={(e) =>
                    handleHeaderChange("isDefinitionLibrary", e.target.checked)
                  }
                >
                  Is Definition Library
                </Checkbox>
              </FormControl>
            </Box>

            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <HStack alignItems="center" mb={4}>
                <Heading size="md">Entries</Heading>
                <Tooltip label="Add Entry">
                  <IconButton
                    icon={<HiOutlinePlus />}
                    size="md"
                    onClick={handleAddEntry}
                    aria-label="Add entry"
                  />
                </Tooltip>
              </HStack>

              {entriesLoading ? (
                <Spinner />
              ) : (
                <Box
                  className="ag-theme-alpine"
                  style={{ height: "400px", width: "100%" }}>
                  <AgGridReact
                    rowData={entries}
                    columnDefs={columnDefs}
                    defaultColDef={{
                      resizable: true,
                    }}
                    pagination={true}
                    paginationPageSize={10}
                    domLayout="normal"
                  />
                </Box>
              )}
            </Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default RegistersEditor;
